import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type {
  EmailArea,
  EmailSettings,
  UpdateEmailSettingsRequest,
} from "@/types/email-settings";

const path = (area: EmailArea) => `/api/admin/email-settings/${area}`;

/** Stable cache key for one area's email settings. */
const key = (area: EmailArea) => ["admin", "email-settings", area] as const;

/**
 * One area's email settings: whether its email really reaches internal staff
 * in this lower environment. Super-admin only, and absent in production (the
 * server returns 404).
 */
export function useEmailSettings(area: EmailArea) {
  return useQuery({
    queryKey: key(area),
    queryFn: () => api.get<EmailSettings>(path(area)),
  });
}

/** Change one area's email settings. */
export function useSetEmailSettings(area: EmailArea) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEmailSettingsRequest) =>
      api.put<EmailSettings>(path(area), input),
    onSuccess: (data) => qc.setQueryData(key(area), data),
  });
}
